import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/florist-dashboard/DashboardLayout';
import { LiveOrdersPanel } from '../../components/dashboard/LiveOrdersPanel';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useStoreProfile } from '@/hooks/useStoreProfile';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Package, DollarSign, Clock } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

const LiveDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { storeProfile, loading, error } = useStoreProfile();
  
  // Use storeProfile?.id directly
  const floristId = storeProfile?.id;

  const [todayStats, setTodayStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  useEffect(() => {
    const fetchTodayStats = async () => {
      if (!floristId) return;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      try {
        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('florist_id', floristId)
          .gte('created_at', today.toISOString());
          
        if (ordersError) throw ordersError;
        
        const stats = {
          totalOrders: orders?.length || 0,
          totalRevenue: orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0,
          pendingOrders: orders?.filter(order => order.status === 'pending').length || 0,
          completedOrders: orders?.filter(order => order.status === 'completed').length || 0
        };
        
        setTodayStats(stats);
      } catch (error) {
        console.error('Error fetching today stats:', error);
      }
    };

    const fetchRecentActivity = async () => {
      if (!floristId) return;
      
      try {
        const { data: activity, error: activityError } = await supabase
          .from('orders')
          .select(`
            *,
            profiles:customer_id (full_name)
          `)
          .eq('florist_id', floristId)
          .order('created_at', { ascending: false })
          .limit(10);
          
        if (activityError) throw activityError;
        
        setRecentActivity(activity || []);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    };

    if (floristId) {
      fetchTodayStats();
      fetchRecentActivity();
    }
  }, [floristId]);

  if (loading || authLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">
          <p className="text-red-500 font-semibold">{error}</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!storeProfile || !floristId) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">
          <p className="text-muted-foreground">No store profile found. Please complete your store setup.</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold">Live Dashboard</h2>
          <p className="text-muted-foreground">Monitor your business in real-time</p>
        </div>
        
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="orders">Pending Orders</TabsTrigger>
            <TabsTrigger value="activity">Recent Activity</TabsTrigger>
            <TabsTrigger value="statistics">Today's Stats</TabsTrigger>
          </TabsList>
          
          <TabsContent value="orders" className="mt-0">
            <LiveOrdersPanel floristId={floristId} limit={15} />
          </TabsContent>
          
          <TabsContent value="activity" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest events and notifications</CardDescription>
              </CardHeader>
              <CardContent>
                {recentActivity.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center gap-4 p-3 rounded-lg border">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            New order from {activity.profiles?.full_name || 'Customer'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(activity.total_amount)}</p>
                          <p className="text-xs text-muted-foreground">Order #{activity.id.slice(0, 8)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="statistics" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle>Today's Statistics</CardTitle>
                <CardDescription>Key metrics for your business</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-primary" />
                        <p className="text-sm font-medium">Total Revenue</p>
                      </div>
                      <p className="text-2xl font-bold mt-2">{formatCurrency(todayStats.totalRevenue)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <p className="text-sm font-medium">Total Orders</p>
                      </div>
                      <p className="text-2xl font-bold mt-2">{todayStats.totalOrders}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <p className="text-sm font-medium">Pending Orders</p>
                      </div>
                      <p className="text-2xl font-bold mt-2">{todayStats.pendingOrders}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <p className="text-sm font-medium">Completed Orders</p>
                      </div>
                      <p className="text-2xl font-bold mt-2">{todayStats.completedOrders}</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default LiveDashboard; 